import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  UserPlus, Check, X, Eye, MagnifyingGlass, PaperPlaneRight, Clock,
  CheckCircle, XCircle, Warning, Lightning, Copy
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-600', icon: Clock },
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700', icon: PaperPlaneRight },
  under_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  activated: { label: 'Activated', color: 'bg-purple-100 text-purple-700', icon: Lightning },
};

export default function OnboardingAdminPage() {
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', phone: '', name: '' });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { fetchApps(); }, [page, statusFilter]);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/onboarding/applications?page=${page}&limit=20&status=${statusFilter}`, { withCredentials: true });
      setApps(data.applications || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load applications'); }
    finally { setLoading(false); }
  };

  const openDetail = async (app) => {
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/onboarding/${app.seller_id}`, { withCredentials: true });
      setDetail(data);
    } catch { setDetail(app); }
    finally { setDetailLoading(false); }
  };

  const handleAction = async (sellerId, action, body = {}) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/admin/onboarding/${sellerId}/${action}`, body, { withCredentials: true });
      toast.success(`Seller ${action}d`);
      fetchApps();
      if (detail?.seller_id === sellerId) openDetail({ seller_id: sellerId });
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
    finally { setActionLoading(false); }
  };

  const handleInvite = async () => {
    if (!inviteForm.email) { toast.error('Email required'); return; }
    try {
      await axios.post(`${API}/api/onboarding/invites`, inviteForm, { withCredentials: true });
      toast.success('Invite sent');
      setInviteDialog(false);
      setInviteForm({ email: '', phone: '', name: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.color}`}><cfg.icon size={12} weight="bold" />{cfg.label}</span>;
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="onboarding-admin-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Seller Onboarding & Approval</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">{total} applications</p>
          </div>
          <Button onClick={() => setInviteDialog(true)} className="bg-[#7d64ff] hover:bg-[#6b4fef]" data-testid="send-invite-btn">
            <UserPlus size={16} className="mr-1.5" /> Send Invite
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#e5e5ea] rounded-xl mb-5">
          <div className="action-bar">
            <Select value={statusFilter || "all"} onValueChange={v => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-44 h-9" data-testid="onboarding-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="activated">Activated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-14 skeleton rounded-xl" />)}</div>
        ) : apps.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <UserPlus size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No onboarding applications found</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#e5e5ea] overflow-hidden">
            <table className="w-full text-sm" data-testid="onboarding-table">
              <thead className="bg-[#f9f9fb] border-b border-[#e5e5ea]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Seller</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Contact</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Progress</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Applied</th>
                  <th className="text-right px-4 py-3 font-medium text-[#6b6b8a]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {apps.map(a => (
                  <tr key={a.seller_id} className="border-b border-[#f0f0f0] hover:bg-[#fafafe]">
                    <td className="px-4 py-3 font-medium text-[#0A0A0A]">{a.legal_name}</td>
                    <td className="px-4 py-3 text-[#6b6b8a] text-xs"><div>{a.email}</div><div>{a.phone}</div></td>
                    <td className="px-4 py-3 text-xs">{a.business_type}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-[#e5e5ea] rounded-full overflow-hidden">
                          <div className="h-full bg-[#7d64ff] rounded-full" style={{ width: `${a.completion_percent || 0}%` }} />
                        </div>
                        <span className="text-xs text-[#9c9db7]">{a.completion_percent || 0}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-3 text-xs text-[#9c9db7]">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(a)} data-testid={`view-app-${a.seller_id}`}><Eye size={16} /></Button>
                      {a.status === 'submitted' && <Button variant="ghost" size="sm" className="text-amber-600" onClick={() => handleAction(a.seller_id, 'review')}><Clock size={16} /></Button>}
                      {['submitted','under_review'].includes(a.status) && (
                        <>
                          <Button variant="ghost" size="sm" className="text-green-600" onClick={() => handleAction(a.seller_id, 'approve', { notes: '' })} data-testid={`approve-${a.seller_id}`}><Check size={16} /></Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { setRejectDialog(a.seller_id); setRejectReason(''); }} data-testid={`reject-${a.seller_id}`}><X size={16} /></Button>
                        </>
                      )}
                      {a.status === 'approved' && <Button variant="ghost" size="sm" className="text-purple-600" onClick={() => handleAction(a.seller_id, 'activate')} data-testid={`activate-${a.seller_id}`}><Lightning size={16} /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <span className="text-sm text-[#6b6b8a] py-1.5">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}

        {/* Detail Drawer */}
        <Sheet open={!!detail || detailLoading} onOpenChange={() => setDetail(null)}>
          <SheetContent side="right" className="w-full sm:max-w-[560px] p-0 overflow-y-auto" data-testid="onboarding-detail-drawer">
            {detailLoading ? (
              <div className="p-6 space-y-4"><div className="h-10 skeleton rounded-xl" /><div className="h-40 skeleton rounded-xl" /><div className="h-40 skeleton rounded-xl" /></div>
            ) : detail && (
              <>
                <div className="sticky top-0 bg-white z-10 border-b border-[#e5e5ea] px-6 py-4">
                  <SheetHeader>
                    <div className="flex items-center justify-between pr-8">
                      <SheetTitle>{detail.legal_name}</SheetTitle>
                      <StatusBadge status={detail.status} />
                    </div>
                    <p className="text-xs text-[#9c9db7]">{detail.email} &middot; {detail.phone}</p>
                  </SheetHeader>
                </div>
                <div className="p-6 space-y-5">
                  {/* Progress */}
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="font-semibold text-[#6b6b8a]">Completion</span>
                      <span className="font-bold text-[#7d64ff]">{detail.completion_percent || 0}%</span>
                    </div>
                    <div className="w-full h-2.5 bg-[#e5e5ea] rounded-full overflow-hidden">
                      <div className="h-full bg-[#7d64ff] rounded-full transition-all" style={{ width: `${detail.completion_percent || 0}%` }} />
                    </div>
                    <div className="grid grid-cols-4 gap-1 mt-2">
                      {['business_details','brand_details','bank_details','warehouse_details','business_model','category_selection','documents','declaration'].map(s => (
                        <div key={s} className={`text-[9px] text-center py-1 rounded ${detail.steps_status?.[s] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>{s.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()).slice(0,12)}</div>
                      ))}
                    </div>
                  </div>

                  {/* KYC */}
                  {detail.kyc && (
                    <div className="bg-white border border-[#e5e5ea] rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-[#6b6b8a] uppercase mb-2">KYC Verification</h3>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between"><span className="text-[#6b6b8a]">PAN</span><span className="font-mono font-medium">{detail.kyc.pan} <span className={detail.kyc.pan_status === 'verified' ? 'text-green-600' : 'text-red-500'}>({detail.kyc.pan_status})</span></span></div>
                        {detail.kyc.gstin && <div className="flex justify-between"><span className="text-[#6b6b8a]">GSTIN</span><span className="font-mono font-medium">{detail.kyc.gstin} <span className={detail.kyc.gst_status === 'verified' ? 'text-green-600' : 'text-red-500'}>({detail.kyc.gst_status})</span></span></div>}
                        <div className="flex justify-between"><span className="text-[#6b6b8a]">KYC Status</span><span className={`font-semibold ${detail.kyc.kyc_status === 'approved' ? 'text-green-600' : detail.kyc.kyc_status === 'rejected' ? 'text-red-500' : 'text-amber-600'}`}>{detail.kyc.kyc_status}</span></div>
                      </div>
                    </div>
                  )}

                  {/* Bank */}
                  {detail.bank && (
                    <div className="bg-white border border-[#e5e5ea] rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-[#6b6b8a] uppercase mb-2">Bank Details</h3>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between"><span className="text-[#6b6b8a]">Account</span><span className="font-mono">{detail.bank.account_number}</span></div>
                        <div className="flex justify-between"><span className="text-[#6b6b8a]">IFSC</span><span className="font-mono">{detail.bank.ifsc_code}</span></div>
                        <div className="flex justify-between"><span className="text-[#6b6b8a]">Holder</span><span>{detail.bank.account_holder_name}</span></div>
                        <div className="flex justify-between"><span className="text-[#6b6b8a]">Verification</span><span className={`font-semibold ${detail.bank.verification_status === 'verified' ? 'text-green-600' : 'text-red-500'}`}>{detail.bank.verification_status}</span></div>
                      </div>
                    </div>
                  )}

                  {/* Brands */}
                  {detail.brands?.length > 0 && (
                    <div className="bg-white border border-[#e5e5ea] rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-[#6b6b8a] uppercase mb-2">Brands ({detail.brands.length})</h3>
                      <div className="space-y-1">{detail.brands.map(b => <div key={b.brand_id} className="text-sm flex justify-between"><span className="font-medium">{b.brand_name}</span><span className="text-xs text-[#9c9db7]">{b.ownership_type}</span></div>)}</div>
                    </div>
                  )}

                  {/* Warehouses */}
                  {detail.warehouses?.length > 0 && (
                    <div className="bg-white border border-[#e5e5ea] rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-[#6b6b8a] uppercase mb-2">Warehouses ({detail.warehouses.length})</h3>
                      <div className="space-y-1">{detail.warehouses.map(w => <div key={w.warehouse_id} className="text-sm"><span className="font-medium">{w.warehouse_name}</span> <span className="text-xs text-[#9c9db7]">{w.city}, {w.pincode}</span></div>)}</div>
                    </div>
                  )}

                  {/* Documents */}
                  {detail.documents?.length > 0 && (
                    <div className="bg-white border border-[#e5e5ea] rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-[#6b6b8a] uppercase mb-2">Documents ({detail.documents.length})</h3>
                      <div className="space-y-1">{detail.documents.map(d => <div key={d.doc_id} className="text-sm flex justify-between items-center"><span>{d.doc_name} <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded">{d.tag}</span></span><a href={d.doc_url} target="_blank" rel="noreferrer" className="text-xs text-[#7d64ff] hover:underline">View</a></div>)}</div>
                    </div>
                  )}

                  {/* Rejection reason */}
                  {detail.rejection_reason && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-red-600 uppercase mb-1">Rejection Reason</h3>
                      <p className="text-sm text-red-700">{detail.rejection_reason}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="space-y-2 pt-2">
                    {detail.status === 'submitted' && <Button onClick={() => handleAction(detail.seller_id, 'review')} className="w-full bg-amber-500 hover:bg-amber-600 text-white" disabled={actionLoading}>Mark Under Review</Button>}
                    {['submitted','under_review'].includes(detail.status) && (
                      <div className="grid grid-cols-2 gap-2">
                        <Button onClick={() => handleAction(detail.seller_id, 'approve', { notes: '' })} className="bg-green-600 hover:bg-green-700 text-white" disabled={actionLoading}><Check size={16} className="mr-1" /> Approve</Button>
                        <Button variant="outline" className="border-red-300 text-red-600" onClick={() => { setRejectDialog(detail.seller_id); setRejectReason(''); }} disabled={actionLoading}><X size={16} className="mr-1" /> Reject</Button>
                      </div>
                    )}
                    {detail.status === 'approved' && <Button onClick={() => handleAction(detail.seller_id, 'activate')} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef] text-white" disabled={actionLoading}><Lightning size={16} className="mr-1" /> Activate Seller</Button>}
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* Reject Dialog */}
        <Dialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
          <DialogContent className="max-w-sm" data-testid="reject-dialog">
            <DialogHeader><DialogTitle>Reject Application</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Reason (mandatory)</Label><textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm min-h-[80px] mt-1" placeholder="Provide reason for rejection..." data-testid="reject-reason" /></div>
              <Button onClick={() => { handleAction(rejectDialog, 'reject', { reason: rejectReason }); setRejectDialog(null); }} disabled={!rejectReason.trim()} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reject-btn">Confirm Rejection</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invite Dialog */}
        <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
          <DialogContent className="max-w-sm" data-testid="invite-dialog">
            <DialogHeader><DialogTitle>Send Seller Invite</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Email *</Label><Input value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} placeholder="seller@company.com" data-testid="invite-email" /></div>
              <div><Label>Name</Label><Input value={inviteForm.name} onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" /></div>
              <div><Label>Phone</Label><Input value={inviteForm.phone} onChange={e => setInviteForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91 98765 43210" /></div>
              <Button onClick={handleInvite} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]" data-testid="send-invite-confirm-btn">Send Invite</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
